import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { QueryKey } from '@/Config/api/queryKey';

export type ActiveToken = {
  token_uuid: string;
  created_by_user_id: number;
  created_timestamp: string;
  expiration_timestamp?: string;
  description: string;
  scopes: string[];
  token?: string; // optionally present; displayed at the top of the card
};

export type LegacyTokenInfo = {
  scenario_id: string;
  api_key: string;
};

export const useApiTokens = (isOpen: boolean) => {
  const queryClient = useQueryClient();
  const [pendingTokenMap, setPendingTokenMap] = useState<Record<string, string>>({});
  const [copiedTokens, setCopiedTokens] = useState<Set<string>>(new Set());
  const [everCopiedTokens, setEverCopiedTokens] = useState<Set<string>>(new Set());

  const { data: tokens = [] } = useQuery<ActiveToken[]>({
    queryKey: [QueryKey.apiTokens.list],
    enabled: isOpen,
  });

  const { data: legacyInfo } = useQuery<LegacyTokenInfo>({
    queryKey: [QueryKey.apiTokens.legacyInfo],
    enabled: isOpen,
  });

  useEffect(() => {
    if (isOpen) {
      // Reset pending tokens only when sheet opens
      setPendingTokenMap((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      setEverCopiedTokens((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      queryClient.invalidateQueries({ queryKey: [QueryKey.apiTokens.list] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.apiTokens.legacyInfo] });
    }
  }, [isOpen, queryClient]);

  const { mutate: createToken, isPending: isCreating } = useMutation<
    { token: string; decoded_data?: { token_uuid?: string } },
    Error,
    { description: string; scopes: string[] }
  >({
    mutationKey: [QueryKey.apiTokens.create],
    onSuccess: (data) => {
      const tokenValue = data.token;
      const tokenUuid = data?.decoded_data?.token_uuid;
      if (tokenUuid) {
        setPendingTokenMap((prev) => ({ ...prev, [tokenUuid]: tokenValue }));
      }
      queryClient.invalidateQueries({ queryKey: [QueryKey.apiTokens.list] });
    },
  });

  const {
    mutate: revokeToken,
    isPending: isRevokingMutation,
    variables: revokingUuid,
  } = useMutation<unknown, Error, string>({
    mutationKey: [QueryKey.apiTokens.revoke],
    onSuccess: (_, tokenUuid) => {
      setPendingTokenMap((prev) => {
        const next = { ...prev };
        delete next[tokenUuid];
        return next;
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.apiTokens.list] });
    },
  });

  const {
    mutate: updateTokenScopes,
    isPending: isUpdatingScopes,
    variables: updatingScopesVars,
  } = useMutation<unknown, Error, { tokenUuid: string; scopes: string[] }>({
    mutationKey: [QueryKey.apiTokens.updateScopes],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.apiTokens.list] });
    },
  });

  const activeTokens = useMemo(
    () =>
      tokens.map((t) =>
        pendingTokenMap[t.token_uuid] ? { ...t, token: pendingTokenMap[t.token_uuid] } : t,
      ),
    [tokens, pendingTokenMap],
  );

  const handleCopyToken = useCallback(async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedTokens((prev) => new Set([...prev, token]));
      setEverCopiedTokens((prev) => new Set([...prev, token]));
      setTimeout(() => {
        setCopiedTokens((prev) => {
          const next = new Set(prev);
          next.delete(token);
          return next;
        });
      }, 2000);
    } catch {
      // noop
    }
  }, []);

  const clearPendingTokens = useCallback(() => {
    setPendingTokenMap({});
    setEverCopiedTokens(new Set());
  }, []);

  const isRevoking = useCallback(
    (uuid: string) => isRevokingMutation && revokingUuid === uuid,
    [isRevokingMutation, revokingUuid],
  );

  const hasUncopiedPendingTokens = useMemo(() => {
    const pendingTokenValues = Object.values(pendingTokenMap);
    return pendingTokenValues.some((token) => !everCopiedTokens.has(token));
  }, [pendingTokenMap, everCopiedTokens]);

  const isUpdatingTokenScopes = useCallback(
    (uuid: string) => isUpdatingScopes && updatingScopesVars?.tokenUuid === uuid,
    [isUpdatingScopes, updatingScopesVars],
  );

  return {
    activeTokens,
    createToken,
    isCreating,
    revokeToken,
    isRevoking,
    updateTokenScopes,
    isUpdatingTokenScopes,
    copiedTokens,
    handleCopyToken,
    clearPendingTokens,
    hasUncopiedPendingTokens,
    legacyInfo,
  };
};
